import { useState, useRef } from 'react'
import {
  X,
  Maximize,
  Printer,
  Download,
  Sparkles,
  Moon,
  Sun,
  ExternalLink,
} from 'lucide-react'
import html2pdf from 'html2pdf.js'
import type { BookingRecord } from '../../../types'

type MeetingBoardModalProps = {
  isOpen: boolean
  onClose: () => void
  booking: BookingRecord | null
}

export const MeetingBoardModal = ({ isOpen, onClose, booking }: MeetingBoardModalProps) => {
  const [isDark, setIsDark] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const boardRef = useRef<HTMLDivElement>(null)

  if (!isOpen || !booking) return null

  const customerName = booking.customer.name.toUpperCase()
  const reference = booking.reference
  // const flightNote = 'FLIGHT OS742'

  const meetingBoardUrl = `/meeting-board?ref=${encodeURIComponent(booking.reference)}&name=${encodeURIComponent(booking.customer.name)}&from=${encodeURIComponent(booking.route.from)}&to=${encodeURIComponent(booking.route.to)}&time=${encodeURIComponent(booking.scheduledAt)}`

  const handleOpenPublicLink = () => {
    window.open(meetingBoardUrl, '_blank')
  }

  const handlePrint = () => {
    window.print()
  }

 
  const handleDownloadPdf = async () => {
    if (!boardRef.current || isDownloading) return

    setIsDownloading(true)
    let staging: HTMLDivElement | null = null

    try {
      // Create a PDF-only copy. The modal remains unchanged.
      const pdfBoard = boardRef.current.cloneNode(true) as HTMLDivElement

      // A4 landscape proportions
      const pdfPage = document.createElement('div')
      pdfPage.style.cssText = `
        width: 1120px;
        height: 780px;
        padding: 24px;
        box-sizing: border-box;
        display: flex;
        background: ${isDark ? '#1A1A1A' : '#F4F4F4'};
      `

      // Enlarge and distribute the existing board content
      pdfBoard.style.width = '100%'
      pdfBoard.style.height = '100%'
      pdfBoard.style.minHeight = '0'
      pdfBoard.style.boxSizing = 'border-box'
      pdfBoard.style.padding = '65px 80px'
      pdfBoard.style.display = 'flex'
      pdfBoard.style.flexDirection = 'column'
      pdfBoard.style.justifyContent = 'space-between'
      pdfBoard.style.alignItems = 'center'
      pdfBoard.style.borderRadius = '20px'
      pdfBoard.style.backgroundColor = isDark ? '#0D0D0D' : '#FFFFFF'

      // Larger logo
      const logo = pdfBoard.querySelector('img')
      if (logo) {
        logo.style.height = '95px'
        logo.style.width = 'auto'
        logo.style.marginBottom = '12px'
      }

      // Logo subtitle
      const subtitle = pdfBoard.querySelector('img + span') as HTMLElement | null
      if (subtitle) {
        subtitle.style.fontSize = '16px'
        subtitle.style.letterSpacing = '0.18em'
      }

      // Bigger customer name
      const name = pdfBoard.querySelector('h1')
      if (name) {
        const nameLength = customerName.length

        name.style.fontSize =
          nameLength > 28 ? '50px' :
          nameLength > 20 ? '60px' : '76px'

        name.style.fontWeight = '900'
        name.style.lineHeight = '1.12'
        name.style.maxWidth = '920px'
        name.style.overflowWrap = 'anywhere'

        // Centered Welcome label
        const welcome = name.previousElementSibling as HTMLElement | null
        if (welcome) {
          welcome.style.fontSize = '20px'
          welcome.style.marginBottom = '18px'
        }

        const nameSection = name.parentElement
        if (nameSection) {
          nameSection.style.margin = '0'
          nameSection.style.padding = '15px 0'
        }
      }

      // Booking reference / company name at the bottom
      const footer = pdfBoard.lastElementChild as HTMLElement | null
      if (footer) {
        footer.style.fontSize = '18px'
        footer.style.paddingTop = '24px'
        footer.style.width = '100%'
        footer.style.flexDirection = 'row'
        footer.style.justifyContent = 'space-between'
      }

      pdfPage.appendChild(pdfBoard)

      // Stage the PDF content outside the visible screen
      staging = document.createElement('div')
      staging.style.cssText =
        'position:fixed; left:-10000px; top:0; pointer-events:none;'

      staging.appendChild(pdfPage)
      document.body.appendChild(staging)

      await document.fonts.ready

      await html2pdf()
        .set({
          margin: 0,
          filename: `Meeting_Board_${booking.reference}.pdf`,
          image: {
            type: 'jpeg',
            quality: 0.98,
          },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: isDark ? '#1A1A1A' : '#F4F4F4',
            windowWidth: 1280,
            scrollX: 0,
            scrollY: 0,
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'landscape',
          },
        })
        .from(pdfPage)
        .save()

    } catch (error) {
      console.error('Meeting board PDF download failed:', error)
      alert('Unable to download the meeting board PDF.')
    } finally {
      staging?.remove()
      setIsDownloading(false)
    }
  }


  const handleFullscreen = () => {
    if (!boardRef.current) return
    if (!document.fullscreenElement) {
      boardRef.current.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err)
      })
    } else {
      document.exitFullscreen()
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm print:p-0 print:bg-white print:static">
      <div className="relative flex flex-col w-full max-w-[840px] rounded-[24px] bg-[#1A1A1A] p-6 shadow-2xl print:max-w-none print:p-0 print:bg-white print:rounded-none">
        
        {/* Controls Bar (hidden on print) */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-4 border-b border-white/10 print:hidden">
          <div className="flex items-center gap-2 text-white">
            <Sparkles size={18} className="text-[#FF6A1A]" />
            <h3 className="font-host text-[16px] font-semibold">Airport Meeting Board</h3>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setIsDark(!isDark)}
              className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 font-host text-[12px] font-medium text-white transition"
              title="Toggle Theme"
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
              {isDark ? 'Light' : 'Dark'}
            </button>

            <button
              onClick={handleFullscreen}
              className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 font-host text-[12px] font-medium text-white transition"
              title="Fullscreen Mode"
            >
              <Maximize size={15} />
              Fullscreen
            </button>

            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 rounded-lg bg-[#719642] hover:bg-[#5f8034] px-3.5 py-1.5 font-host text-[12px] font-medium text-white transition shadow-sm"
              title="Print Sign"
            >
              <Printer size={15} />
              Print Sign
            </button>

            {/* Download Meeting Board PDF */}
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="flex items-center gap-1.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] px-3.5 py-1.5 font-host text-[12px] font-medium text-white transition shadow-sm disabled:opacity-50"
              title="Download Meeting Board as PDF"
            >
              <Download size={15} />
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </button>

            <button
              onClick={handleOpenPublicLink}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3.5 py-1.5 font-host text-[12px] font-medium text-white transition shadow-sm"
              title="Open Public Driver Board in new tab"
            >
              <ExternalLink size={14} />
              Driver Link
            </button>

            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition ml-2"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Meeting Board Display Canvas */}
        <div
          ref={boardRef}
          className={`relative w-full rounded-[20px] p-8 sm:p-14 transition-colors flex flex-col justify-between items-center text-center select-none shadow-inner print:shadow-none print:p-8 ${
            isDark ? 'bg-[#0D0D0D] text-white' : 'bg-white text-[#111827]'
          }`}
          style={{ minHeight: '440px' }}
        >
          {/* Logo Header */}
          <div className="flex flex-col items-center">
            <img
              src="/assets/images/logo.png"
              alt="Salzburg Cab"
              className="h-[55px] object-contain mb-2"
            />
            <span className={`text-[12px] font-host tracking-[0.2em] font-semibold uppercase ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              VIP Chauffeur & Private Transfer
            </span>
          </div>

          {/* Passenger Name Section */}
          <div className="my-8 py-6 px-4 w-full">
            <p className={`font-host text-[14px] font-medium tracking-[0.25em] uppercase mb-2 ${
              isDark ? 'text-[#FF6A1A]' : 'text-[#719642]'
            }`}>
              Welcome
            </p>
            <h1 className="font-host text-[34px] sm:text-[46px] font-black tracking-tight leading-tight uppercase">
              {customerName}
            </h1>
          </div>

          {/* Footer Details */}
          <div className={`w-full pt-4 border-t flex flex-col sm:flex-row items-center justify-between text-[13px] font-host ${
            isDark ? 'border-white/10 text-gray-400' : 'border-gray-200 text-gray-500'
          }`}>
            <div className="flex items-center gap-4">
              {/* <span>{flightNote}</span>
              <span>•</span> */}
              <span>Ref #{reference}</span>
            </div>

            <div className="mt-2 sm:mt-0 font-medium">
              Salzburg Cab Service
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
